<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Kullanıcılar"
      title="Puan yönetimi"
      description="Lider tablosu, puan geçmişi, APR snapshotları ve manuel puan operasyonlarını tek bir yönetim yüzeyinde takip edin."
    >
      <template #actions>
        <v-btn
          outlined
          color="primary"
          small
          :loading="activeTab === 2 ? aprLoading : loading"
          @click="activeTab === 2 ? fetchAprSnapshots(true) : fetchOverview()"
        >
          {{ activeTab === 2 ? 'Snapshotları Yenile' : 'Yenile' }}
        </v-btn>
      </template>
    </admin-page-header>

    <v-alert
      v-if="errorMessage"
      dense
      outlined
      type="error"
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>

    <admin-metric-strip
      :items="summaryCards"
      :loading="loading"
    />

    <v-card
      class="admin-table-card"
      outlined
    >
      <v-tabs
        v-model="activeTab"
        class="admin-tabs"
      >
        <v-tab>Sıralama</v-tab>
        <v-tab>Puan Geçmişi</v-tab>
        <v-tab>APR Snapshotları</v-tab>
      </v-tabs>

      <v-toolbar
        dense
        :elevation="0"
        class="admin-page-toolbar"
      >
        <v-toolbar-title>
          {{ toolbarTitle }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          v-if="activeTab !== 2"
          color="primary"
          :loading="submitting"
          @click="dialog = true"
        >
          Manuel Puan Ekle
        </v-btn>
      </v-toolbar>

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <v-card-text class="pa-6 pt-0">
            <v-data-table
              :headers="leaderboardHeaders"
              :items="leaderboard"
              :loading="loading"
              :footer-props="tableFooterProps"
              class="admin-data-table elevation-0"
            >
              <template v-slot:item.rank="{ item }">
                <span
                  class="points-rank"
                  :class="`points-rank--${Math.min(item.rank, 4)}`"
                >
                  {{ item.rank }}
                </span>
              </template>

              <template v-slot:item.user="{ item }">
                <div class="d-flex align-center">
                  <v-avatar
                    color="primary"
                    size="36"
                    class="mr-3"
                  >
                    <span class="white--text text-caption font-weight-bold">
                      {{ item.initials }}
                    </span>
                  </v-avatar>
                  <div>
                    <div class="font-weight-bold">
                      {{ item.fullName }}
                    </div>
                    <div class="text-caption text--secondary">
                      #{{ item.userId }}
                    </div>
                  </div>
                </div>
              </template>

              <template v-slot:item.email="{ item }">
                <span class="text-caption">
                  {{ item.email }}
                </span>
              </template>

              <template v-slot:item.total="{ item }">
                <span class="font-weight-bold">
                  {{ formatPoints(item.totalPoints) }}
                </span>
              </template>

              <template v-slot:item.entryCount="{ item }">
                <span class="text-caption">
                  {{ formatNumber(item.entryCount) }}
                </span>
              </template>

              <template v-slot:item.lastEffectiveAt="{ item }">
                <span class="font-mono text-caption">
                  {{ formatTimestamp(item.lastEffectiveAt) }}
                </span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tab-item>

        <v-tab-item>
          <v-card-text class="pa-6 pt-0">
            <v-data-table
              :headers="historyHeaders"
              :items="historyEntries"
              :loading="loading"
              :footer-props="tableFooterProps"
              class="admin-data-table elevation-0"
            >
              <template v-slot:item.effectiveAt="{ item }">
                <span class="font-mono text-caption">
                  {{ formatTimestamp(item.effectiveAt) }}
                </span>
              </template>

              <template v-slot:item.user="{ item }">
                <div>
                  <div class="font-weight-medium">
                    {{ item.fullName }}
                  </div>
                  <div class="text-caption text--secondary">
                    {{ item.email }}
                  </div>
                </div>
              </template>

              <template v-slot:item.amount="{ item }">
                <span
                  :class="Number(item.amount) >= 0 ? 'points-positive' : 'points-negative'"
                >
                  {{ formatSignedPoints(item.amount) }}
                </span>
              </template>

              <template v-slot:item.subWalletType="{ item }">
                <v-chip
                  x-small
                  label
                  outlined
                >
                  {{ subWalletTypeLabel(item.subWalletType) }}
                </v-chip>
              </template>

              <template v-slot:item.status="{ item }">
                <v-chip
                  x-small
                  label
                  :color="item.status === 'POSTED' ? 'success' : 'warning'"
                >
                  {{ item.status === 'POSTED' ? 'İşlendi' : 'Bekliyor' }}
                </v-chip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tab-item>

        <v-tab-item>
          <v-card-text class="pa-6 pt-0">
            <v-row class="mb-2">
              <v-col
                v-for="item in aprSummaryCards"
                :key="item.label"
                cols="12"
                md="4"
              >
                <v-card
                  class="admin-dashboard-card points-summary-card"
                  outlined
                >
                  <v-card-text class="d-flex align-center">
                    <div class="points-summary-card__icon">
                      <v-icon>{{ item.icon }}</v-icon>
                    </div>
                    <div>
                      <div class="text-caption text--secondary">
                        {{ item.label }}
                      </div>
                      <div class="text-h6 font-weight-bold mt-1">
                        {{ item.value }}
                      </div>
                      <div class="text-caption text--secondary mt-1">
                        {{ item.helper }}
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-alert
              v-if="aprError"
              dense
              outlined
              type="error"
              class="mb-4"
            >
              {{ aprError }}
            </v-alert>

            <v-card
              outlined
              class="mb-6"
            >
              <v-card-text class="pa-6">
                <div class="d-flex flex-wrap align-center justify-space-between mb-4">
                  <div>
                    <div class="text-subtitle-1 font-weight-bold">
                      Kullanıcı Bazlı APR Kontrolü
                    </div>
                    <div class="text-caption text--secondary mt-1">
                      User ID ile canlı preview, persisted snapshot ve manual recompute akışını aynı panelden çalıştırın.
                    </div>
                  </div>
                  <v-chip
                    v-if="aprSelectedUserId"
                    small
                    label
                    color="primary"
                    outlined
                    class="mt-2 mt-sm-0"
                  >
                    Son seçim: user {{ aprSelectedUserId }}
                  </v-chip>
                </div>

                <v-row align="end">
                  <v-col
                    cols="12"
                    md="3"
                  >
                    <v-text-field
                      v-model="aprUserIdInput"
                      outlined
                      dense
                      label="User ID"
                      placeholder="örn. 2451"
                      type="number"
                      @keyup.enter="loadAprPreview"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="9"
                  >
                    <div class="d-flex flex-wrap">
                      <v-btn
                        outlined
                        color="primary"
                        class="mr-2 mb-2"
                        :loading="aprPreviewState.loading"
                        @click="loadAprPreview"
                      >
                        Preview Getir
                      </v-btn>
                      <v-btn
                        outlined
                        color="primary"
                        class="mr-2 mb-2"
                        :loading="aprSnapshotState.loading"
                        @click="loadAprSnapshot"
                      >
                        Snapshot Getir
                      </v-btn>
                      <v-btn
                        color="primary"
                        class="mb-2"
                        :loading="aprRecomputeLoading"
                        @click="requestAprRecompute"
                      >
                        Recompute
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col
                    cols="12"
                    xl="6"
                  >
                    <v-card
                      outlined
                      class="apr-result-card"
                    >
                      <v-card-title class="text-subtitle-1 font-weight-bold">
                        Canlı Preview
                      </v-card-title>
                      <v-card-subtitle class="pt-0">
                        Engine mevcut user state üzerinden anlık hesap yapar.
                      </v-card-subtitle>
                      <v-divider></v-divider>
                      <v-card-text class="pa-4">
                        <div
                          v-if="aprPreviewState.loading"
                          class="apr-empty-state"
                        >
                          Preview yükleniyor...
                        </div>
                        <v-alert
                          v-else-if="aprPreviewState.error"
                          dense
                          outlined
                          type="error"
                          class="mb-0"
                        >
                          {{ aprPreviewState.error }}
                        </v-alert>
                        <div v-else-if="aprPreviewState.data">
                          <div class="font-weight-bold">
                            {{ aprPreviewState.data.fullName || '-' }}
                          </div>
                          <div class="text-caption text--secondary">
                            {{ aprPreviewState.data.email || '-' }}
                          </div>
                          <div class="d-flex flex-wrap mt-3">
                            <v-chip
                              small
                              label
                              class="mr-2 mb-2"
                              :color="aprPreviewState.data.blocked ? 'error' : 'primary'"
                              :outlined="!aprPreviewState.data.blocked"
                              :dark="aprPreviewState.data.blocked"
                            >
                              {{ aprPreviewState.data.eligibilityBucket || 'Unknown' }}
                            </v-chip>
                            <v-chip
                              small
                              label
                              outlined
                              class="mr-2 mb-2"
                            >
                              {{ aprPreviewState.data.premiumAccess ? 'Premium' : 'Standart' }}
                            </v-chip>
                            <v-chip
                              v-if="aprPreviewState.data.devicePlatform"
                              small
                              label
                              outlined
                              class="mr-2 mb-2"
                            >
                              {{ aprPreviewState.data.devicePlatform }}
                            </v-chip>
                          </div>

                          <v-simple-table dense>
                            <tbody>
                              <tr
                                v-for="row in buildAprMetricRows(aprPreviewState.data)"
                                :key="`preview-${row.label}`"
                              >
                                <td class="text-caption text--secondary">
                                  {{ row.label }}
                                </td>
                                <td class="text-right font-weight-medium">
                                  {{ row.value }}
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>

                          <div class="mt-4">
                            <div class="text-caption text--secondary mb-2">
                              Gate Sebepleri
                            </div>
                            <div
                              v-if="aprPreviewState.data.gateReasons && aprPreviewState.data.gateReasons.length"
                              class="d-flex flex-wrap"
                            >
                              <v-chip
                                v-for="reason in aprPreviewState.data.gateReasons"
                                :key="`preview-reason-${reason}`"
                                small
                                label
                                outlined
                                class="mr-2 mb-2"
                              >
                                {{ reason }}
                              </v-chip>
                            </div>
                            <div
                              v-else
                              class="text-caption text--secondary"
                            >
                              Gate blokajı görünmüyor.
                            </div>
                          </div>

                          <div class="mt-4">
                            <div class="text-caption text--secondary mb-2">
                              Tier 2 Önerileri
                            </div>
                            <div
                              v-if="aprPreviewState.data.tier2Recommendations && aprPreviewState.data.tier2Recommendations.length"
                            >
                              <div
                                v-for="recommendation in aprPreviewState.data.tier2Recommendations"
                                :key="`preview-tier-${recommendation.bucketId}`"
                                class="apr-recommendation"
                              >
                                <div class="d-flex align-center justify-space-between flex-wrap">
                                  <div class="font-weight-medium">
                                    {{ recommendation.label || recommendation.bucketId || '-' }}
                                  </div>
                                  <div class="text-caption text--secondary mt-1 mt-sm-0">
                                    Aktif APR: {{ effectiveRecommendationApr(recommendation, aprPreviewState.data.premiumAccess) }}
                                  </div>
                                </div>
                                <div class="text-caption text--secondary mt-2">
                                  Aralık: {{ effectiveRecommendationRange(recommendation, aprPreviewState.data.premiumAccess) }}
                                </div>
                              </div>
                            </div>
                            <div
                              v-else
                              class="text-caption text--secondary"
                            >
                              Kullanıcı için öneri bulunamadı.
                            </div>
                          </div>
                        </div>
                        <div
                          v-else
                          class="apr-empty-state"
                        >
                          User ID girip preview isteği çalıştırın.
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col
                    cols="12"
                    xl="6"
                  >
                    <v-card
                      outlined
                      class="apr-result-card"
                    >
                      <v-card-title class="text-subtitle-1 font-weight-bold">
                        Latest Snapshot
                      </v-card-title>
                      <v-card-subtitle class="pt-0">
                        Persisted snapshot read-model. Recompute sonrası kalıcı state’i görmek için kullanın.
                      </v-card-subtitle>
                      <v-divider></v-divider>
                      <v-card-text class="pa-4">
                        <div
                          v-if="aprSnapshotState.loading"
                          class="apr-empty-state"
                        >
                          Snapshot yükleniyor...
                        </div>
                        <v-alert
                          v-else-if="aprSnapshotState.error"
                          dense
                          outlined
                          type="error"
                          class="mb-0"
                        >
                          {{ aprSnapshotState.error }}
                        </v-alert>
                        <div v-else-if="aprSnapshotState.data">
                          <div class="font-weight-bold">
                            {{ aprSnapshotState.data.fullName || '-' }}
                          </div>
                          <div class="text-caption text--secondary">
                            {{ aprSnapshotState.data.email || '-' }}
                          </div>
                          <div class="d-flex flex-wrap mt-3">
                            <v-chip
                              small
                              label
                              class="mr-2 mb-2"
                              :color="aprSnapshotState.data.blocked ? 'error' : 'primary'"
                              :outlined="!aprSnapshotState.data.blocked"
                              :dark="aprSnapshotState.data.blocked"
                            >
                              {{ aprSnapshotState.data.eligibilityBucket || 'Unknown' }}
                            </v-chip>
                            <v-chip
                              small
                              label
                              outlined
                              class="mr-2 mb-2"
                            >
                              {{ aprSnapshotState.data.premiumAccess ? 'Premium' : 'Standart' }}
                            </v-chip>
                            <v-chip
                              v-if="aprSnapshotState.data.devicePlatform"
                              small
                              label
                              outlined
                              class="mr-2 mb-2"
                            >
                              {{ aprSnapshotState.data.devicePlatform }}
                            </v-chip>
                          </div>

                          <v-simple-table dense>
                            <tbody>
                              <tr
                                v-for="row in buildAprMetricRows(aprSnapshotState.data)"
                                :key="`snapshot-${row.label}`"
                              >
                                <td class="text-caption text--secondary">
                                  {{ row.label }}
                                </td>
                                <td class="text-right font-weight-medium">
                                  {{ row.value }}
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>

                          <div class="mt-4">
                            <div class="text-caption text--secondary mb-2">
                              Assumptions
                            </div>
                            <div
                              v-if="aprSnapshotState.data.assumptions && aprSnapshotState.data.assumptions.length"
                              class="d-flex flex-wrap"
                            >
                              <v-chip
                                v-for="assumption in aprSnapshotState.data.assumptions"
                                :key="`snapshot-assumption-${assumption}`"
                                small
                                label
                                outlined
                                class="mr-2 mb-2"
                              >
                                {{ assumption }}
                              </v-chip>
                            </div>
                            <div
                              v-else
                              class="text-caption text--secondary"
                            >
                              Snapshot assumption kaydı bulunmuyor.
                            </div>
                          </div>

                          <div class="mt-4">
                            <div class="text-caption text--secondary mb-2">
                              Tier 2 Önerileri
                            </div>
                            <div
                              v-if="aprSnapshotState.data.tier2Recommendations && aprSnapshotState.data.tier2Recommendations.length"
                            >
                              <div
                                v-for="recommendation in aprSnapshotState.data.tier2Recommendations"
                                :key="`snapshot-tier-${recommendation.bucketId}`"
                                class="apr-recommendation"
                              >
                                <div class="d-flex align-center justify-space-between flex-wrap">
                                  <div class="font-weight-medium">
                                    {{ recommendation.label || recommendation.bucketId || '-' }}
                                  </div>
                                  <div class="text-caption text--secondary mt-1 mt-sm-0">
                                    Aktif APR: {{ effectiveRecommendationApr(recommendation, aprSnapshotState.data.premiumAccess) }}
                                  </div>
                                </div>
                                <div class="text-caption text--secondary mt-2">
                                  Aralık: {{ effectiveRecommendationRange(recommendation, aprSnapshotState.data.premiumAccess) }}
                                </div>
                              </div>
                            </div>
                            <div
                              v-else
                              class="text-caption text--secondary"
                            >
                              Kullanıcı için öneri bulunamadı.
                            </div>
                          </div>
                        </div>
                        <div
                          v-else
                          class="apr-empty-state"
                        >
                          User ID girip persisted snapshot isteği çalıştırın.
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-data-table
              :headers="aprSnapshotHeaders"
              :items="aprSnapshots"
              :loading="aprLoading"
              :footer-props="tableFooterProps"
              :items-per-page="10"
              class="admin-data-table elevation-0"
              no-data-text="Gösterilecek APR snapshot kaydı bulunamadı."
            >
              <template v-slot:item.user="{ item }">
                <div>
                  <div class="font-weight-medium">
                    {{ item.fullName || '-' }}
                  </div>
                  <div class="text-caption text--secondary">
                    {{ item.email || '-' }}
                  </div>
                  <div class="text-caption text--secondary mt-1">
                    User ID: {{ item.userId }}
                  </div>
                </div>
              </template>

              <template v-slot:item.eligibilityBucket="{ item }">
                <div class="d-flex flex-wrap">
                  <v-chip
                    small
                    label
                    class="mr-2 mb-2"
                    :color="item.blocked ? 'error' : 'primary'"
                    :outlined="!item.blocked"
                    :dark="item.blocked"
                  >
                    {{ item.eligibilityBucket || 'Unknown' }}
                  </v-chip>
                  <v-chip
                    v-if="item.blocked"
                    small
                    label
                    color="error"
                    outlined
                    class="mr-2 mb-2"
                  >
                    Blocked
                  </v-chip>
                </div>
              </template>

              <template v-slot:item.score="{ item }">
                <div class="font-weight-medium">
                  {{ formatScore(item.score) }}
                </div>
                <div class="text-caption text--secondary mt-1">
                  Eff.: {{ formatPercent(item.balanceEfficiency) }}
                </div>
              </template>

              <template v-slot:item.premiumAccess="{ item }">
                <v-chip
                  small
                  label
                  :color="item.premiumAccess ? 'primary' : 'grey lighten-3'"
                  :dark="item.premiumAccess"
                  :outlined="!item.premiumAccess"
                >
                  {{ item.premiumAccess ? 'Premium' : 'Standart' }}
                </v-chip>
                <div class="text-caption text--secondary mt-1">
                  {{ item.devicePlatform || 'Unknown' }}
                </div>
              </template>

              <template v-slot:item.rewardEligibleBalanceTl="{ item }">
                <div class="font-weight-medium">
                  {{ formatCurrency(item.rewardEligibleBalanceTl) }}
                </div>
                <div class="text-caption text--secondary mt-1">
                  CAC Payback: {{ formatPercent(item.cacPaybackRatio) }}
                </div>
              </template>

              <template v-slot:item.cm90ContributionTl="{ item }">
                {{ formatCurrency(item.cm90ContributionTl) }}
              </template>

              <template v-slot:item.activeFlexibleStandardApr="{ item }">
                <div>Std: <span class="font-weight-medium">{{ formatPercent(item.activeFlexibleStandardApr) }}</span></div>
                <div class="text-caption text--secondary mt-1">
                  Prm: <span class="font-weight-medium">{{ formatPercent(item.activeFlexiblePremiumApr) }}</span>
                </div>
              </template>

              <template v-slot:item.recordedAt="{ item }">
                <div class="font-weight-medium">
                  {{ formatTimestamp(item.recordedAt) }}
                </div>
                <div class="text-caption text--secondary mt-1">
                  Aktif: {{ formatTimestamp(item.effectiveFrom) }}
                </div>
                <div class="text-caption text--secondary">
                  Bitiş: {{ formatTimestamp(item.effectiveUntil) }}
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <v-dialog
      v-model="dialog"
      max-width="520px"
      persistent
    >
      <v-card class="admin-dialog-card">
        <v-toolbar
          dense
          :elevation="0"
        >
          <v-toolbar-title>Manuel Puan Ekle</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            :disabled="submitting"
            title="Puan formunu kapat"
            aria-label="Puan formunu kapat"
            @click="dialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <v-alert
            v-if="errorMessage"
            type="error"
            dense
            outlined
          >
            {{ errorMessage }}
          </v-alert>
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="manualForm.user"
                :items="manualUsers"
                :search-input.sync="manualUserSearch"
                item-text="display"
                return-object
                outlined
                dense
                label="Kullanıcı"
                placeholder="Ad, soyad veya e-posta ile ara"
                :loading="manualUserLoading"
                :disabled="submitting"
                hide-no-data
                @update:search-input="onManualUserSearch"
              ></v-autocomplete>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model.number="manualForm.points"
                outlined
                dense
                type="number"
                label="Puan Miktarı"
                :disabled="submitting"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <div v-if="manualBalance !== null">
                Mevcut: <strong>{{ formatNumber(manualBalance) }} ADVp</strong><br>İşlem sonrası tahmini: <strong>{{ formatNumber(manualBalance + Number(manualForm.points || 0)) }} ADVp</strong>
              </div>
              <v-progress-linear
                v-if="manualBalanceLoading"
                indeterminate
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="manualForm.reason"
                outlined
                dense
                rows="3"
                label="İşlem gerekçesi"
                :disabled="submitting"
                maxlength="500"
              ></v-textarea>
            </v-col>
          </v-row>
          <v-checkbox
            v-model="manualConfirmed"
            :disabled="submitting || manualBalanceLoading || manualBalance === null"
            label="Kullanıcıyı, miktarı ve gerekçeyi kontrol ettim."
          />
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-spacer></v-spacer>
          <v-btn
            text
            color="primary"
            :disabled="submitting"
            @click="dialog = false"
          >
            İptal
          </v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="submitting || !manualConfirmed || manualBalanceLoading || manualBalance === null"
            @click="submitManualPoints"
          >
            Puanı ekle
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="aprConfirmDialog"
      max-width="480"
      persistent
    >
      <admin-confirm-dialog
        title="APR yeniden hesaplama"
        :item-name="`Kullanıcı #${aprConfirmUserId}`"
        description="Bu kullanıcı için puan getirisi ve APR kaydı yeniden hesaplanacak."
        confirm-text="Yeniden hesapla"
        :save="recomputeAprUser"
        :close="closeAprConfirmation"
      />
    </v-dialog>
    <v-snackbar
      v-model="snackbar"
      timeout="3500"
      color="info"
    >
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios'
import {
  mdiClose,
  mdiMedalOutline,
  mdiStarFourPointsOutline,
  mdiTrophyOutline,
} from '@mdi/js'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminMetricStrip from '@/components/admin/AdminMetricStrip.vue'
import AdminConfirmDialog from '@/components/admin/AdminConfirmDialog.vue'

const APR_SNAPSHOT_LIMIT = 50

const createAprPanelState = () => ({
  loading: false,
  error: '',
  data: null,
})

export default {
  name: 'PointsManagement',
  components: {
    AdminConfirmDialog,
    AdminPageHeader,
    AdminMetricStrip,
  },
  data() {
    return {
      loading: false,
      submitting: false,
      activeTab: 1,
      manualBalance: null,
      manualBalanceLoading: false,
      manualBalanceRequest: 0,
      manualConfirmed: false,
      dialog: false,
      snackbar: false,
      snackbarMessage: '',
      errorMessage: '',
      manualUserSearch: '',
      manualUserLoading: false,
      manualUserLookupTimeout: null,
      icons: {
        mdiClose,
      },
      summary: {
        usersWithPoints: 0,
        postedPoints: 0,
        pendingPoints: 0,
        totalEntries: 0,
      },
      leaderboardHeaders: [
        { text: '#', value: 'rank', sortable: false },
        { text: 'Kullanıcı', value: 'user', sortable: false },
        { text: 'Email', value: 'email', sortable: false },
        { text: 'Toplam Puan', value: 'total', sortable: false },
        { text: 'Kayıt', value: 'entryCount', sortable: false },
        { text: 'Son Hareket', value: 'lastEffectiveAt', sortable: false },
      ],
      leaderboard: [],
      historyHeaders: [
        { text: 'Tarih', value: 'effectiveAt', sortable: false },
        { text: 'Kullanıcı', value: 'user', sortable: false },
        { text: 'Puan', value: 'amount', sortable: false },
        { text: 'Cüzdan', value: 'subWalletType', sortable: false },
        { text: 'Durum', value: 'status', sortable: false },
        { text: 'Açıklama', value: 'description', sortable: false },
      ],
      historyEntries: [],
      aprLoading: false,
      aprLoaded: false,
      aprError: '',
      aprSnapshots: [],
      aprGeneratedAt: '',
      aprItemCount: 0,
      aprUserIdInput: '',
      aprSelectedUserId: null,
      aprPreviewState: createAprPanelState(),
      aprSnapshotState: createAprPanelState(),
      aprRecomputeLoading: false,
      aprConfirmDialog: false,
      aprConfirmUserId: null,
      aprSnapshotHeaders: [
        { text: 'Kullanıcı', value: 'user', sortable: false },
        { text: 'Bucket', value: 'eligibilityBucket', sortable: false },
        { text: 'Skor', value: 'score', sortable: false },
        { text: 'Erişim', value: 'premiumAccess', sortable: false },
        { text: 'Reward Eligible', value: 'rewardEligibleBalanceTl', sortable: false },
        { text: 'CM90', value: 'cm90ContributionTl', sortable: false },
        { text: 'Esnek APR', value: 'activeFlexibleStandardApr', sortable: false },
        { text: 'Snapshot', value: 'recordedAt', sortable: false },
      ],
      manualUsers: [],
      manualForm: {
        user: null,
        points: null,
        reason: '',
        date: '',
      },
    }
  },
  computed: {
    summaryCards() {
      const leader = this.leaderboard[0]

      return [
        {
          label: 'Puanı olan kullanıcı',
          value: this.formatNumber(this.summary.usersWithPoints),
          helper: 'Wallet point ledger ilişkisi olan kullanıcı',
          icon: mdiStarFourPointsOutline,
        },
        {
          label: 'İşlenmiş puan',
          value: this.formatPoints(this.summary.postedPoints),
          helper: 'POSTED statüsündeki toplam puan',
          icon: mdiTrophyOutline,
        },
        {
          label: 'Lider kullanıcı',
          value: leader ? `${leader.fullName}` : '-',
          helper: leader ? this.formatPoints(leader.totalPoints) : 'Henüz puan kaydı yok',
          icon: mdiMedalOutline,
        },
      ]
    },
    toolbarTitle() {
      return {
        0: 'Lider tablosu',
        1: 'Puan hareketleri',
        2: 'APR snapshotları',
      }[this.activeTab] || 'Puan yönetimi'
    },
    aprSummaryCards() {
      const blockedCount = this.aprSnapshots.filter(item => item.blocked).length
      const averageScore = this.aprSnapshots.length
        ? this.aprSnapshots.reduce((total, item) => total + Number(item.score || 0), 0) / this.aprSnapshots.length
        : 0

      return [
        {
          label: 'Snapshot Kullanıcısı',
          value: this.formatNumber(this.aprItemCount),
          helper: `${this.aprSnapshots.length} kayıt ekrana geldi`,
          icon: mdiStarFourPointsOutline,
        },
        {
          label: 'Blocked Kullanıcı',
          value: this.formatNumber(blockedCount),
          helper: 'APR gate ile bloklanan kullanıcılar',
          icon: mdiTrophyOutline,
        },
        {
          label: 'Ortalama Skor',
          value: this.formatScore(averageScore),
          helper: this.aprGeneratedAt ? `Son liste: ${this.formatTimestamp(this.aprGeneratedAt)}` : 'Henüz snapshot alınmadı',
          icon: mdiMedalOutline,
        },
      ]
    },
    tableFooterProps() {
      return {
        'items-per-page-options': [10, 20, 50, 100],
      }
    },
  },
  watch: {
    'manualForm.user': function onManualUserChange() { this.manualConfirmed = false; this.loadManualBalance() },
    'manualForm.points': function onManualPointsChange() { this.manualConfirmed = false },
    'manualForm.reason': function onManualReasonChange() { this.manualConfirmed = false },
    activeTab(value) {
      if (value === 2 && !this.aprLoaded && !this.aprLoading) {
        this.fetchAprSnapshots()
      }
    },
  },
  created() {
    this.fetchOverview()
  },
  methods: {
    async loadManualBalance() {
      this.manualBalanceRequest += 1
      const request = this.manualBalanceRequest
      this.manualBalance = null
      const id = this.manualForm.user?.id
      if (!id) {
        this.manualBalanceLoading = false

        return
      }
      this.manualBalanceLoading = true
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}panel/users/${id}/dashboard`)
        if (request !== this.manualBalanceRequest) return
        const balance = response.data?.data?.points?.totalPoints
        if (balance === null || balance === undefined || !Number.isFinite(Number(balance))) throw new Error('Puan bakiyesi doğrulanamadı.')
        this.manualBalance = Number(balance)
      } catch (error) { if (request === this.manualBalanceRequest) this.errorMessage = 'Puan bakiyesi yüklenemedi. Kullanıcıyı yeniden seçin.' } finally { if (request === this.manualBalanceRequest) this.manualBalanceLoading = false }
    },
    formatNumber(value) {
      return Number(value).toLocaleString('tr-TR')
    },
    formatPoints(value) {
      return `${this.formatNumber(Number(value || 0))} puan`
    },
    formatSignedPoints(value) {
      const amount = Number(value || 0)
      const prefix = amount >= 0 ? '+' : ''

      return `${prefix}${this.formatNumber(amount)}`
    },
    formatScore(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }

      return Number(value).toLocaleString('tr-TR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    },
    formatPercent(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }

      return `${Number(value).toLocaleString('tr-TR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}%`
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }

      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(Number(value))
    },
    formatTimestamp(value) {
      if (!value) {
        return '-'
      }

      let parsedDate = null

      if (value instanceof Date) {
        parsedDate = value
      } else if (typeof value === 'number') {
        parsedDate = new Date(value)
      } else if (typeof value === 'string') {
        const normalized = value.trim()
        const ddMmYyyyMatch = normalized.match(/^(\d{2})\.(\d{2})\.(\d{4})(?: (\d{2}):(\d{2})(?::(\d{2}))?)?$/)

        if (ddMmYyyyMatch) {
          const [, day, month, year, hour = '00', minute = '00', second = '00'] = ddMmYyyyMatch
          parsedDate = new Date(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hour),
            Number(minute),
            Number(second),
          )
        } else {
          parsedDate = new Date(normalized)
        }
      } else {
        parsedDate = new Date(value)
      }

      if (!(parsedDate instanceof Date) || Number.isNaN(parsedDate.getTime())) {
        return typeof value === 'string' ? value : '-'
      }

      return new Intl.DateTimeFormat('tr-TR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(parsedDate)
    },
    subWalletTypeLabel(value) {
      return {
        FLEXIBLE: 'Esnek',
        EXPECTED: 'Beklenen',
        LOCKED: 'Kilitli',
        REWARD: 'Ödül',
      }[value] || value || '-'
    },
    initialsOf(item) {
      const parts = (item.fullName || '').split(' ').filter(Boolean)

      return parts.slice(0, 2).map(part => part[0]).join('').toUpperCase() || 'AP'
    },
    applyOverview(payload) {
      this.summary = (payload && payload.summary) || this.summary
      this.leaderboard = (((payload && payload.leaderboard) || [])).map(item => ({
        ...item,
        initials: this.initialsOf(item),
      }))
      this.historyEntries = (payload && payload.history) || []
    },
    async fetchOverview() {
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/points/overview`)
        this.applyOverview(response && response.data ? response.data.data : null)
      } catch (error) {
        this.errorMessage = error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Puan yönetimi verileri yüklenemedi.'
      } finally {
        this.loading = false
      }
    },
    applyAprSnapshots(payload) {
      this.aprSnapshots = Array.isArray(payload && payload.items) ? payload.items : []
      this.aprGeneratedAt = (payload && payload.generatedAt) || ''
      this.aprItemCount = Number((payload && payload.itemCount) || 0)
      this.aprLoaded = true
    },
    parseAprUserId() {
      const normalized = String(this.aprUserIdInput || '').trim()
      const parsed = Number(normalized)

      if (!normalized || Number.isNaN(parsed) || parsed <= 0) {
        return null
      }

      return parsed
    },
    buildAprMetricRows(payload) {
      return [
        { label: 'Skor', value: this.formatScore(payload && payload.score) },
        { label: 'Reward Eligible', value: this.formatCurrency(payload && payload.rewardEligibleBalanceTl) },
        { label: 'CM90', value: this.formatCurrency(payload && payload.cm90ContributionTl) },
        { label: 'Lifetime CM', value: this.formatCurrency(payload && payload.lifetimeContributionTl) },
        { label: 'CAC', value: this.formatCurrency(payload && payload.assumedCacTl) },
        { label: 'CAC Payback', value: this.formatPercent(payload && payload.cacPaybackRatio) },
        { label: 'Balance Efficiency', value: this.formatPercent(payload && payload.balanceEfficiency) },
        { label: '2. İşlem', value: payload && payload.secondTransactionObserved ? 'Var' : 'Yok' },
        { label: '90g Conversion', value: this.formatNumber((payload && payload.confirmedConversions90Days) || 0) },
        { label: '90g Click', value: this.formatNumber((payload && payload.clicks90Days) || 0) },
        { label: 'Suspicious Rate', value: this.formatPercent(payload && payload.suspiciousClickRate) },
        { label: 'Confidence', value: (payload && payload.incrementalityConfidence) || '-' },
        { label: 'Recorded', value: this.formatTimestamp(payload && payload.recordedAt) },
      ]
    },
    effectiveRecommendationApr(recommendation, premiumAccess) {
      let resolved = recommendation && recommendation.standardApr

      if (premiumAccess) {
        if (recommendation && recommendation.premiumApr !== null && recommendation.premiumApr !== undefined) {
          resolved = recommendation.premiumApr
        }
      } else if (!(recommendation && recommendation.standardApr !== null && recommendation.standardApr !== undefined)) {
        resolved = recommendation && recommendation.premiumApr
      }

      return this.formatPercent(resolved)
    },
    effectiveRecommendationRange(recommendation, premiumAccess) {
      let floor = recommendation && recommendation.standardFloorApr
      let ceiling = recommendation && recommendation.standardCeilingApr

      if (premiumAccess) {
        if (recommendation && recommendation.premiumFloorApr !== null && recommendation.premiumFloorApr !== undefined) {
          floor = recommendation.premiumFloorApr
        }
        if (recommendation && recommendation.premiumCeilingApr !== null && recommendation.premiumCeilingApr !== undefined) {
          ceiling = recommendation.premiumCeilingApr
        }
      } else {
        if (!(recommendation && recommendation.standardFloorApr !== null && recommendation.standardFloorApr !== undefined)) {
          floor = recommendation && recommendation.premiumFloorApr
        }
        if (!(recommendation && recommendation.standardCeilingApr !== null && recommendation.standardCeilingApr !== undefined)) {
          ceiling = recommendation && recommendation.premiumCeilingApr
        }
      }

      if (floor === null || floor === undefined || ceiling === null || ceiling === undefined) {
        return '-'
      }

      return `${this.formatPercent(floor)} - ${this.formatPercent(ceiling)}`
    },
    async fetchAprSnapshots(force = false) {
      if (this.aprLoading || (this.aprLoaded && !force)) {
        return
      }

      this.aprLoading = true
      this.aprError = ''
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/points/apr-engine/snapshots`, {
          params: {
            limit: APR_SNAPSHOT_LIMIT,
          },
        })
        this.applyAprSnapshots(response && response.data ? response.data.data : null)
      } catch (error) {
        this.aprError = error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'APR snapshot listesi alınamadı.'
      } finally {
        this.aprLoading = false
      }
    },
    async runAprUserRequest(userId, pathSuffix, method, stateKey) {
      this[stateKey] = {
        loading: true,
        error: '',
        data: null,
      }

      try {
        const url = `${process.env.VUE_APP_BASE_API_URL}admin/points/apr-engine/users/${userId}${pathSuffix}`
        const response = method === 'POST'
          ? await axios.post(url)
          : await axios.get(url)
        const data = response && response.data ? response.data.data : null
        this[stateKey] = {
          loading: false,
          error: '',
          data,
        }

        return data
      } catch (error) {
        this[stateKey] = {
          loading: false,
          error: error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'APR isteği başarısız oldu.',
          data: null,
        }

        return null
      }
    },
    async loadAprPreview() {
      const userId = this.parseAprUserId()

      if (!userId) {
        this.aprPreviewState = {
          loading: false,
          error: 'Geçerli bir User ID girin.',
          data: null,
        }

        return
      }

      this.aprSelectedUserId = userId
      await this.runAprUserRequest(userId, '', 'GET', 'aprPreviewState')
    },
    async loadAprSnapshot() {
      const userId = this.parseAprUserId()

      if (!userId) {
        this.aprSnapshotState = {
          loading: false,
          error: 'Geçerli bir User ID girin.',
          data: null,
        }

        return
      }

      this.aprSelectedUserId = userId
      await this.runAprUserRequest(userId, '/snapshot', 'GET', 'aprSnapshotState')
    },
    requestAprRecompute() {
      if (this.aprRecomputeLoading) return
      const userId = this.parseAprUserId()
      if (!userId) {
        this.aprError = 'Yeniden hesaplamak için geçerli bir kullanıcı ID girin.'

        return
      }
      this.aprConfirmUserId = userId
      this.aprConfirmDialog = true
    },
    closeAprConfirmation() { if (!this.aprRecomputeLoading) this.aprConfirmDialog = false },
    async recomputeAprUser() {
      if (!this.aprConfirmDialog || !this.aprConfirmUserId || this.aprRecomputeLoading) return false
      const userId = this.aprConfirmUserId
      this.aprSelectedUserId = userId
      this.aprRecomputeLoading = true
      this.aprError = ''
      try {
        const recomputed = await this.runAprUserRequest(userId, '/recompute', 'POST', 'aprPreviewState')
        if (recomputed) {
          await Promise.all([
            this.runAprUserRequest(userId, '/snapshot', 'GET', 'aprSnapshotState'),
            this.fetchAprSnapshots(true),
          ])
          this.snackbarMessage = 'APR recompute tamamlandı.'
          this.snackbar = true
        }

        return recomputed
      } finally {
        this.aprRecomputeLoading = false
      }
    },
    onManualUserSearch(value) {
      if (!value || value.length < 2) {
        return
      }

      clearTimeout(this.manualUserLookupTimeout)
      this.manualUserLookupTimeout = setTimeout(() => {
        this.fetchManualUsers(value)
      }, 250)
    },
    async fetchManualUsers(search) {
      this.manualUserLoading = true
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}user`, {
          params: {
            page: 0,
            size: 12,
            sortBy: 'created',
            direction: 'DESC',
            searchKey: search,
          },
        })
        this.manualUsers = (((response && response.data && response.data.content) || [])).map(item => ({
          id: item.id,
          fullName: [item.name, item.surname].filter(Boolean).join(' ').trim() || '-',
          email: item.email || '-',
          display: `${[item.name, item.surname].filter(Boolean).join(' ').trim() || '-'} · ${item.email || '-'}`,
        }))
      } catch (error) {
        this.errorMessage = error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Kullanıcı araması yapılamadı.'
      } finally {
        this.manualUserLoading = false
      }
    },
    async submitManualPoints() {
      if (this.submitting || !this.manualConfirmed || this.manualBalanceLoading || this.manualBalance === null) return
      const isInvalidManualForm = !this.manualForm.user
        || !this.manualForm.points
        || Number(this.manualForm.points) <= 0
        || !Number.isFinite(Number(this.manualForm.points))
        || !String(this.manualForm.reason || '').trim()

      if (isInvalidManualForm) {
        this.errorMessage = 'Kullanıcı, sıfırdan büyük puan miktarı ve işlem gerekçesi zorunlu.'
      } else {
        this.submitting = true
        this.errorMessage = ''
        try {
          const response = await axios.post(`${process.env.VUE_APP_BASE_API_URL}admin/points/manual-adjustment`, {
            userId: this.manualForm.user.id,
            points: this.manualForm.points,
            reason: this.manualForm.reason,
          })
          this.applyOverview(response && response.data ? response.data.data : null)
          this.dialog = false
          this.manualForm = {
            user: null,
            points: null,
            reason: '',
            date: '',
          }
          this.manualUserSearch = ''
          this.manualUsers = []
          this.snackbarMessage = 'Manuel puan kaydı oluşturuldu.'
          this.snackbar = true
        } catch (error) {
          this.errorMessage = error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Manuel puan kaydı oluşturulamadı.'
        } finally {
          this.submitting = false
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.points-summary-card {
  min-height: 140px;
}

.points-summary-card__icon {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  flex-shrink: 0;
}

.points-rank {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
}

.points-rank--1 {
  background: #ffd700;
  color: #0f172a;
}

.points-rank--2 {
  background: #c0c0c0;
  color: #0f172a;
}

.points-rank--3 {
  background: #cd7f32;
  color: white;
}

.points-rank--4 {
  background: rgba(148, 163, 184, 0.16);
  color: inherit;
}

.points-positive {
  color: #56ca00;
  font-weight: 700;
}

.points-negative {
  color: #ff4c51;
  font-weight: 700;
}

.apr-result-card {
  height: 100%;
}

.apr-empty-state {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: rgba(71, 85, 105, 0.88);
}

.apr-recommendation {
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 14px;
  padding: 12px 14px;

  & + & {
    margin-top: 10px;
  }
}
</style>
